import { maxWeightMatching, type WeightedEdge } from '../matching/blossom.js';
import { isPlayedResult, pointsFor, type Score } from '../model/types.js';
import { allocateColoursWithRule, colourState, type ColourRule } from './colour.js';
import { DutchEngine } from './dutch/engine.js';
import { buildDutchPlayers, type DutchPlayer } from './dutch/state.js';
import {
  PairingError,
  type Pair,
  type PairingEngine,
  type PairingPlayer,
  type PairingRequest,
  type PairingResult,
} from './types.js';

/**
 * FIDE Burstein System, C.04.4.2 as applied from 1 February 2026.
 *
 * The first min(floor(rounds / 2), 4) rounds are seeding rounds, paired with
 * the Dutch system (1.6). Later rounds rank the players of a bracket by an
 * opposition Index (Buchholz, then Sonneborn-Berger, then TPN; 1.8) and pair
 * high Index against low Index within each scoregroup.
 *
 * Method, as for the Dutch engine: each bracket is decided by one
 * maximum-weight matching over the bracket and every lower player, so the
 * round can always be completed ([C4]). Edge weights pack, most significant
 * first: completion, [C5] pairs in the bracket, [C6] scores of the paired
 * players, [C7] pairs and scores in the next bracket, [C8] colour
 * preferences, and finally the order of Article 4 (BSN 1's opponent as low
 * in the ranking as possible, then BSN 2's, and so on; floating last).
 */

export const BURSTEIN_COLOUR_RULE: Record<ColourRule, { article: string; text: string }> = {
  '5.2.5': { article: '5.2.1', text: 'neither has played: initial colour by TPN parity' },
  '5.2.1': { article: '5.2.2', text: 'both colour preferences granted' },
  '5.2.2': { article: '5.2.3', text: 'the stronger colour preference granted' },
  '5.2.3': { article: '5.2.4', text: 'colours alternated from the last round they differed' },
  '5.2.4': { article: '5.2.5', text: "the higher-ranked player's preference granted" },
};

/** Number of seeding rounds paired with the Dutch system (1.6.2). */
export function bursteinSeedingRounds(totalRounds: number): number {
  return Math.min(Math.floor(totalRounds / 2), 4);
}

export interface OppositionIndex {
  /** Buchholz in tenths. */
  readonly buchholz: number;
  /** Sonneborn-Berger in hundredths (points x score, both in tenths). */
  readonly sonnebornBerger: number;
}

/**
 * The opposition Index of every player (1.7). Scores are current scores
 * without virtual (acceleration) points. A round a player did not play counts
 * as a game against themselves with the registered result; a series of
 * zero-point byes reaching the current round counts as draws when that
 * player's score is used for their actual opponents (1.7.2).
 */
export function oppositionIndex(request: PairingRequest): Map<number, OppositionIndex> {
  const ps = request.pointSystem;
  const played = request.round - 1;
  const points = new Map<number, Score>();
  const forOpponents = new Map<number, Score>();
  for (const p of request.players) {
    let s = 0;
    for (let r = 0; r < played && r < p.history.length; r++) {
      s += pointsFor((p.history[r] as PairingPlayer['history'][number]).result, ps);
    }
    points.set(p.id, s);
    let trailingZero = 0;
    for (let r = Math.min(played, p.history.length) - 1; r >= 0; r--) {
      if ((p.history[r] as PairingPlayer['history'][number]).result !== 'Z') break;
      trailingZero++;
    }
    forOpponents.set(p.id, s + trailingZero * ps.draw);
  }
  const out = new Map<number, OppositionIndex>();
  for (const p of request.players) {
    const own = points.get(p.id) as Score;
    let buchholz = 0;
    let sonnebornBerger = 0;
    for (let r = 0; r < played && r < p.history.length; r++) {
      const e = p.history[r] as PairingPlayer['history'][number];
      const earned = pointsFor(e.result, ps);
      const oppScore =
        isPlayedResult(e.result) && e.opponent !== null ? (forOpponents.get(e.opponent) ?? 0) : own;
      buchholz += oppScore;
      sonnebornBerger += earned * oppScore;
    }
    out.set(p.id, { buchholz, sonnebornBerger });
  }
  return out;
}

const bitsFor = (x: bigint): bigint => {
  let b = 0n;
  let v = x;
  while (v > 0n) {
    v >>= 1n;
    b++;
  }
  return b;
};
const one = (b: boolean): bigint => (b ? 1n : 0n);

interface Field {
  readonly width: bigint;
  readonly value: (h: DutchPlayer, l: DutchPlayer) => bigint;
}

/** [C1] and [C3] (no topscorer exception in Burstein). */
function compatible(a: DutchPlayer, b: DutchPlayer): boolean {
  if (a.cannotMeet.has(b.id) || b.cannotMeet.has(a.id)) return false;
  return !(a.strength === 'absolute' && b.strength === 'absolute' && a.preference === b.preference);
}

function solve(vertices: readonly DutchPlayer[], fields: readonly Field[]): Map<number, number> {
  const edges: WeightedEdge[] = [];
  for (let i = 0; i < vertices.length; i++) {
    const h = vertices[i] as DutchPlayer;
    for (let j = i + 1; j < vertices.length; j++) {
      const l = vertices[j] as DutchPlayer;
      if (!compatible(h, l)) continue;
      let w = 0n;
      for (const f of fields) w = (w << f.width) + f.value(h, l);
      edges.push({ u: i, v: j, w });
    }
  }
  const mate = maxWeightMatching(vertices.length, edges);
  const partner = new Map<number, number>();
  for (let i = 0; i < vertices.length; i++) {
    const m = mate[i] as number;
    if (m >= 0) partner.set((vertices[i] as DutchPlayer).id, (vertices[m] as DutchPlayer).id);
  }
  return partner;
}

/** Can these players all be paired with each other ([C1], [C3])? */
function completable(players: readonly DutchPlayer[]): boolean {
  if (players.length % 2 === 1) return false;
  if (players.length === 0) return true;
  const partner = solve(players, [{ width: 1n, value: () => 1n }]);
  return partner.size === players.length;
}

export class BursteinEngine implements PairingEngine {
  readonly id = 'burstein';
  readonly name = 'FIDE Burstein (C.04.4.2, 2026)';

  pair(request: PairingRequest): PairingResult {
    const seeding = bursteinSeedingRounds(request.totalRounds);
    if (request.round <= seeding) {
      const dutch = new DutchEngine().pair(request);
      return {
        ...dutch,
        notes: [...dutch.notes, `seeding round ${request.round} of ${seeding}: Dutch system (1.6)`],
        explanation: [
          `Round ${request.round} is a seeding round (1.6: the first ${seeding}), paired with the Dutch system.`,
          ...(dutch.explanation ?? []),
        ],
      };
    }

    const index = oppositionIndex(request);
    const players = buildDutchPlayers(request);
    const notes: string[] = [];
    const explanation: string[] = [];
    if (players.length === 0) return { pairs: [], byes: [], notes };

    // Ranking order (1.8): Buchholz, Sonneborn-Berger, TPN.
    const rankOf = new Map<number, number>();
    [...players]
      .sort((a, b) => {
        const ia = index.get(a.id) as OppositionIndex;
        const ib = index.get(b.id) as OppositionIndex;
        return ib.buchholz - ia.buchholz || ib.sonnebornBerger - ia.sonnebornBerger || a.id - b.id;
      })
      .forEach((p, i) => rankOf.set(p.id, i));
    const rank = (p: DutchPlayer): number => rankOf.get(p.id) as number;
    const fmt = (x: number): string => (x / 10).toFixed(1);

    // Pairing-allocated bye first (1.9.2, 3.1).
    let byes: number[] = [];
    let pool = players;
    if (players.length % 2 === 1) {
      const candidates = players
        .filter((p) => p.pabEligible)
        .sort((a, b) => a.score - b.score || b.playedGames - a.playedGames || rank(b) - rank(a));
      const bye = candidates.find((c) => completable(players.filter((p) => p !== c)));
      if (bye === undefined) {
        throw new PairingError(
          'no pairing satisfies the absolute criteria (C.04.4.2 1.9.3: the Chief Arbiter decides)',
        );
      }
      byes = [bye.id];
      pool = players.filter((p) => p !== bye);
      explanation.push(
        `Pairing-allocated bye: ${bye.id} (3.1: eligible, leaves a complete pairing, lowest score ${fmt(bye.score)}, most games, lowest ranking).`,
      );
    } else if (!completable(players)) {
      throw new PairingError(
        'no pairing satisfies the absolute criteria (C.04.4.2 1.9.3: the Chief Arbiter decides)',
      );
    }

    const n = pool.length;
    const countWidth = bitsFor(BigInt(n + 2));
    const counts = new Map<number, number>();
    for (const p of pool) counts.set(p.score, (counts.get(p.score) ?? 0) + 1);
    const shift = new Map<number, bigint>();
    let shiftWidth = 0n;
    for (const s of [...counts.keys()].sort((a, b) => a - b)) {
      shift.set(s, shiftWidth);
      shiftWidth += bitsFor(BigInt((counts.get(s) as number) + 1));
    }
    shiftWidth += 1n;
    const pow = (p: DutchPlayer): bigint => 1n << (shift.get(p.score) as bigint);

    const finalPairs: [DutchPlayer, DutchPlayer][] = [];
    let remaining = [...pool].sort((a, b) => b.score - a.score || a.id - b.id);
    let floaters: DutchPlayer[] = [];
    const groupScores = [...counts.keys()].sort((a, b) => b - a);

    for (let g = 0; g < groupScores.length; g++) {
      const s = groupScores[g] as number;
      const nextScore = groupScores[g + 1];
      const bracket = [...floaters, ...remaining.filter((p) => p.score === s)].sort(
        (a, b) => rank(a) - rank(b),
      );
      if (bracket.length === 0) continue;
      const bsn = new Map(bracket.map((p, i) => [p.id, i + 1]));
      const nextSet = new Set(
        nextScore === undefined
          ? []
          : remaining.filter((p) => p.score === nextScore).map((p) => p.id),
      );
      const inBracket = (h: DutchPlayer, l: DutchPlayer): boolean => bsn.has(h.id) && bsn.has(l.id);
      const inNext = (h: DutchPlayer, l: DutchPlayer): boolean =>
        !inBracket(h, l) && (bsn.has(h.id) || nextSet.has(h.id)) && nextSet.has(l.id);
      const B = BigInt(bracket.length + 2);
      const fields: Field[] = [
        // [C4] completion: every edge counts.
        { width: countWidth, value: () => 1n },
        // [C5] maximise the pairs in the bracket.
        { width: countWidth, value: (h, l) => one(inBracket(h, l)) },
        // [C6] minimise the scores of the outgoing floaters.
        { width: shiftWidth, value: (h, l) => (inBracket(h, l) ? pow(h) + pow(l) : 0n) },
        // [C7] the next bracket: its pairs, then the scores paired there.
        { width: countWidth, value: (h, l) => one(inNext(h, l)) },
        { width: shiftWidth, value: (h, l) => (inNext(h, l) ? pow(h) + pow(l) : 0n) },
        // [C8] colour preferences granted.
        {
          width: countWidth,
          value: (h, l) =>
            one(
              inBracket(h, l) &&
                (h.preference === null || l.preference === null || h.preference !== l.preference),
            ),
        },
        // Article 4: BSN 1's opponent as large as possible, then BSN 2's, ...
        {
          width: bitsFor(B ** BigInt(bracket.length)) + 1n,
          value: (h, l) => {
            if (!inBracket(h, l)) return 0n;
            const a = bsn.get(h.id) as number;
            const b = bsn.get(l.id) as number;
            const lo = Math.min(a, b);
            const hi = Math.max(a, b);
            return BigInt(hi) * B ** BigInt(bracket.length - lo);
          },
        },
      ];
      const partner = solve(remaining, fields);
      const paired = new Set<number>();
      const byId = new Map(remaining.map((p) => [p.id, p]));
      const pairsHere: [DutchPlayer, DutchPlayer][] = [];
      for (const p of bracket) {
        const q = partner.get(p.id);
        if (q === undefined || !bsn.has(q) || paired.has(p.id)) continue;
        const other = byId.get(q) as DutchPlayer;
        pairsHere.push([p, other]);
        paired.add(p.id);
        paired.add(q);
      }
      finalPairs.push(...pairsHere);
      const outgoing = bracket.filter((p) => !paired.has(p.id));
      const parts = [
        `Bracket ${fmt(s)}: ${bracket
          .map((p) => {
            const i = index.get(p.id) as OppositionIndex;
            return `${p.id} (BH ${fmt(i.buchholz)}, SB ${(i.sonnebornBerger / 100).toFixed(2)})`;
          })
          .join(', ')}`,
      ];
      if (pairsHere.length > 0)
        parts.push(`pairs ${pairsHere.map(([a, b]) => `${a.id}-${b.id}`).join(' ')}`);
      parts.push(`floaters ${outgoing.map((p) => p.id).join(', ') || 'none'}`);
      explanation.push(parts.join('; ') + '.');
      remaining = remaining.filter((p) => !paired.has(p.id));
      floaters = outgoing;
    }
    if (floaters.length > 0) {
      throw new PairingError('the round-pairing cannot be completed');
    }

    const ordered = orderBoards(finalPairs, request, rankOf);
    for (const o of ordered) {
      const r = BURSTEIN_COLOUR_RULE[o.rule];
      explanation.push(
        `Board ${o.board}: ${o.pair.white}-${o.pair.black}, colours: ${r.text} (Art. ${r.article}).`,
      );
    }
    return { pairs: ordered.map((o) => o.pair), byes, notes, explanation };
  }
}

/** Colours (Article 5, ranking per 1.8) and board order (score, sum, TPN). */
function orderBoards(
  pairs: readonly [DutchPlayer, DutchPlayer][],
  request: PairingRequest,
  rankOf: ReadonlyMap<number, number>,
): { board: number; pair: Pair; rule: ColourRule }[] {
  const byId = new Map(request.players.map((p) => [p.id, p]));
  const r = (p: DutchPlayer): number => rankOf.get(p.id) as number;
  // Board order: the general ranking (score, then TPN) of the pair's top player.
  const top = (a: DutchPlayer, b: DutchPlayer): DutchPlayer =>
    a.score > b.score || (a.score === b.score && a.id < b.id) ? a : b;
  const sorted = pairs
    .map(([a, b]) => (r(a) < r(b) ? ([a, b] as const) : ([b, a] as const)))
    .sort(([a1, b1], [a2, b2]) => {
      const t1 = top(a1, b1);
      const t2 = top(a2, b2);
      return t2.score - t1.score || a2.score + b2.score - (a1.score + b1.score) || t1.id - t2.id;
    });
  return sorted.map(([higher, lower], i) => {
    const { colour: c, rule } = allocateColoursWithRule(
      colourState(byId.get(higher.id)?.history ?? []),
      colourState(byId.get(lower.id)?.history ?? []),
      higher.id,
      request.initialColour,
    );
    const pair =
      c === 'w' ? { white: higher.id, black: lower.id } : { white: lower.id, black: higher.id };
    return { board: i + 1, pair, rule };
  });
}
